import React, { useRef, useMemo } from 'react';
import {
  Box,
  ButtonV2,
  ColumnsV2,
  DialogV2,
  Flex,
  FormControl,
  InputV2,
  Spinner,
  SpacingStack,
  TextArea,
  TextV2,
  TooltipV2,
  LinkV2,
  CancelButton,
  CheckboxV2,
  SelectV1,
  LinkV1,
  IconV2,
} from '@withjoy/joykit';
import { Divider } from '@apps/registry/common/components';
import { ReactComponent as Camera } from '@assets/icons/camera2.svg';
import { useEditGiftDialogController } from './EditGift.controller';
import type { CookedProduct } from '@apps/registry/common/selectors/ProductListSelector';
import { isPricedProduct } from '@apps/registry/common/selectors/ProductListSelector';
import {
  EditPhotoButton,
  StyleQtyBox,
  StyledButtonsContainer,
  StyledFlexPriceHeader,
  StyledLink,
  StyledLinkV2,
  StyledPhoto,
  StyledPhotoText,
  StyledPriceQtyContainer,
  StyledStackPhoto,
  TooltipIcon,
} from './EditGiftDialog.styles';
import { preventNonNumericalInput } from '@shared/utils/preventNonNumericalInput';
import type { OnRemoveRegistryItemHandler } from '@apps/registry/admin/Registry.types';
import { ReactComponent as Help } from '@assets/icons/help.svg';
import type { RegistryOrderList } from '@apps/registry/common/state/RegistryProducts';
import { MsrpTooltip } from '@apps/registry/common/components/MsrpTooltip';
import { pxToRem } from '@withjoy/joykit/theme';
import type { DonationFundPlatformTypeEnum } from '@graphql/generated-base';
import { StyledInputPrefix } from '@apps/registry/common/components/AddCashFund/AddCashFundDialog.styles';
import { paymentMethodOptions } from '@shared/utils/paymentMethodOptions';
import { useSharedRegistryTranslations } from '@apps/registry/SharedRegistry.i18n';
import { getPaymentLink, getPaymentUsernameFormat } from '@apps/registry/common/components/AddCashFund/helpers';
import type { DonationFundInfoFragmentWithUrl } from '@apps/registry/common/components/Catalog/components/ProductGrid/ProductGrid.controller';
import { useFeatureValue } from '@shared/core/featureFlags';
import { useAdminRegistryTelemetry } from '@apps/registry/admin/AdminRegistry.telemetry';
import { useTranslation } from '@shared/core';
import { Skeleton } from '@shared/components/Skeleton';
import { WarningTriangle } from '@withjoy/joykit/icons';
import { FormattedCurrency } from '@shared/utils/currency';
import { SelectPurchaseOptions } from './components/SelectPurchaseOptions';
import { usePrepareAdminSHopUrl } from './usePrepareAdminShopUrl';
import { GROUP_GIFT_HELP_REDIRECTION_LINK } from './EditGift.constant';
import { RegistryItemSource } from '@apps/registry/common/components/Catalog/Catalog.constants';
import { useMount } from '@shared/utils/hooks/useMount';
import { AppPaymentDisclaimerDialog } from '@apps/registry/common/components/AddCashFund/components/CashFundPaymentMethodV2/components/AppPaymentToggle/components/AppPaymentDisclaimerDialog/AppPaymentDisclaimerDialog';
const GROUP_GIFTING_MIN_PRICE = 350;

export interface EditGiftDialogProps
  extends Readonly<{
    currency: string;
    eventId: string;
    product: CookedProduct | null;
    showAddressReminderDialog?: () => void;
    onRemove?: OnRemoveRegistryItemHandler;
    showCurrencyDialog: () => void;
    orders?: RegistryOrderList;
    isOpen: boolean;
    onClose: () => void;
    donationFundInfo?: DonationFundInfoFragmentWithUrl;
    prefilledUrl?: string;
  }> {}

const localPaymentOptions = paymentMethodOptions.filter(method => method.value !== 'cashOrCheck' && method.value !== 'joyCredit');

export const DialogInternal: React.FC<EditGiftDialogProps> = ({
  currency,
  eventId,
  product,
  showAddressReminderDialog,
  onRemove,
  showCurrencyDialog,
  orders,
  isOpen,
  onClose,
  donationFundInfo,
  prefilledUrl,
}) => {
  const {
    formik,
    handleTooltipClick,
    registryItemLoading,
    openFilepicker,
    updatedPhoto,
    handleDeleteRegistryItem,
    handleConfirm,
    handleCancel,
    setPaymentMethod,
    setPaymentMethodUsername,
    selectedPaymentMethod,
    isPaymentMethodWarningShown,
    handleBrandClick,
    extractDataFromUrl,
    isProductExtractionLoading,
    prefilledData,
    showExtractError,
    handleChangeFromPurchaseOption,
    onCloseDisclaimerDialog,
    isOpenDisclaimerDialog,
    disclaimerFormik,
  } = useEditGiftDialogController({
    eventId,
    isOpen,
    product,
    showAddressReminderDialog,
    onRemove,
    onClose,
    showCurrencyDialog,
    donationFundInfo,
    prefilledUrl,
  });

  const { errors } = formik || {};

  const { getCashFundDialogTranslations } = useSharedRegistryTranslations();
  const { t, t2 } = useTranslation('sharedRegistry');
  const { t: translations } = useTranslation('catalogRegistry');
  const { message } = translations('salesPrice');
  const {
    addGiftTitle,
    editGiftTitle,
    titleField,
    qtyField,
    noteField,
    noteFieldPlaceholder,
    photoField,
    addGiftButton,
    cancelButton,
    deleteButton,
    saveGiftButton,
    changeCurrencyTitle,
    currencyLinkText,
    pdpLinkText,
    groupGiftingDescription,
    paymentSettings,
    selectPaymentMethod,
    groupGiftingTooltip,
    moreDetails,
    msrpTitle,
    msrpDescription,
    groupGift,
    markGiftAs,
    mostWanted,
    mostWantedDescription,
    autocompleteError,
  } = t2('giftDialog');
  const { newPaymentMethodWarning, paymentMethodLabel } = getCashFundDialogTranslations();
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isNewGift = !product?.id;
  const isCatalogItem = !isNewGift && !product?.externallyOwned && !product?.canEditProductData;
  const title = isNewGift ? addGiftTitle : editGiftTitle;
  const confirmButton = isNewGift ? addGiftButton : saveGiftButton;

  const currencyBodyText = t(
    'giftDialog',
    'changeCurrencyBody',
  )({
    currencyLink: () => <StyledLink onClick={handleTooltipClick}>{currencyLinkText}</StyledLink>,
  });

  const msrpLabel = t('giftDialog', 'msrpLabel')({ currency });
  const errorLink = formik.touched.link && !!errors.link ? formik.errors.link : undefined;
  const errorTitle = formik.touched.title && !!errors.title ? formik.errors.title : undefined;
  const { value: adminRegistryGetCashEquivalentEnabled } = useFeatureValue('adminRegistryGetCashEquivalentEnabled');
  const featureGetCashEquivalentEnabled = adminRegistryGetCashEquivalentEnabled === 'on';
  const isGroupGiftingFeatureEnabled = useMemo(
    () => Boolean((product?.numberPrice && product.numberPrice >= GROUP_GIFTING_MIN_PRICE) || product?.isGroupGiftingEnabled),
    [product],
  );
  const { enableGroupGifting: trackEnableGroupGifting, enableMustHave: trackEnableMustHave, groupGiftingPaymentMethodSelect, inputFieldChange } = useAdminRegistryTelemetry();
  const adminShopUrl = usePrepareAdminSHopUrl(product);
  const canRemoveProduct = !(orders?.length || isNewGift);
  const isSingleItem = formik.getFieldProps('qty').value === 1;

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    productScannedExist: boolean,
    fieldPropsOnChange: Function,
    label: 'ProductTitle' | 'ProductPrice',
    metadataSource: RegistryItemSource,
    originalValue: string,
    productUrl: string | undefined,
  ) => {
    if (productScannedExist) {
      // if start typing we clear out the timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // log input change after 3 seconds
      timeoutRef.current = setTimeout(() => {
        inputFieldChange({
          currentValue: e.target.value,
          label,
          metadataSource,
          originalValue,
          productUrl,
        });
      }, 3000);
    }

    fieldPropsOnChange(e);
  };

  useMount(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  });

  return (
    <>
      <DialogV2.Header>
        <TextV2 typographyVariant="hed2">{title}</TextV2>
        <DialogV2.CloseButton onClick={onClose} />
      </DialogV2.Header>
      <Divider margin="0 0 1rem 0" />
      <DialogV2.Body>
        {showExtractError && (
          <Box
            backgroundColor="warning1"
            display="flex"
            columnGap="1rem"
            padding={5}
            marginBottom={5}
            borderRadius={3}
          >
            <WarningTriangle color="warning6" />
            <TextV2
              typographyVariant="body1"
              color="warn8"
            >
              {autocompleteError}
            </TextV2>
          </Box>
        )}

        <ColumnsV2>
          <ColumnsV2.Column>
            {isProductExtractionLoading ? (
              <Skeleton
                height={pxToRem(328)}
                width="auto"
                marginBottom={pxToRem(10)}
                marginX="auto"
              />
            ) : (
              <StyledPhoto
                backgroundImage={updatedPhoto}
                backgroundColor="mono2"
                width="auto"
                height={pxToRem(328)}
                marginBottom={pxToRem(10)}
                marginX="auto"
              >
                <div id="preview-photo"></div>
                <StyledStackPhoto spacing={5}>
                  <EditPhotoButton
                    backgroundImage={updatedPhoto}
                    onClick={openFilepicker}
                  >
                    {<Camera />}
                  </EditPhotoButton>
                  {!updatedPhoto ? (
                    <StyledPhotoText
                      typographyVariant="body1"
                      color="mono6"
                    >
                      {photoField}
                    </StyledPhotoText>
                  ) : undefined}
                </StyledStackPhoto>
              </StyledPhoto>
            )}

            {!!adminShopUrl && (
              <Box
                marginX="auto"
                marginTop={5}
                marginBottom={3}
                textAlign="center"
              >
                <StyledLinkV2
                  color="mono14"
                  _visited={{ color: 'mono14' }}
                  typographyVariant="hed1"
                  href={adminShopUrl}
                  target="__blank"
                >
                  {pdpLinkText}
                </StyledLinkV2>
              </Box>
            )}
          </ColumnsV2.Column>
          <ColumnsV2.Column marginTop={[24, 24, 0]}>
            <form>
              <SpacingStack spacing={7}>
                {isCatalogItem ? (
                  <SpacingStack spacing={3}>
                    {product?.brand && (
                      <TextV2
                        typographyVariant="hed3"
                        onClick={handleBrandClick}
                        cursor="pointer"
                      >
                        {product.brand}
                      </TextV2>
                    )}
                    <TextV2 typographyVariant="hed5">{product?.title}</TextV2>
                    <Flex width="min-content">
                      <TextV2
                        fontWeight={500}
                        textDecoration={!product.isFullPrice ? 'line-through' : ''}
                        typographyVariant="hed4"
                        color="mono13"
                        marginRight={2}
                      >
                        {isPricedProduct(product) && (
                          <FormattedCurrency
                            priceFloatingPointDecimalString={!product.isFullPrice && product.fullPrice ? product.fullPrice : product.floatingPointDecimalString}
                            priceCurrencyCode={product.currencyCode}
                            formatForm="short"
                            shouldCalculateCharmPrice={true}
                          />
                        )}
                      </TextV2>
                      {!product.isFullPrice && (
                        <TextV2
                          as="span"
                          fontWeight={500}
                          width="max-content"
                          typographyVariant="hed4"
                          color="#D80B0B"
                          marginRight={2}
                        >
                          {message()}{' '}
                          <FormattedCurrency
                            priceFloatingPointDecimalString={product.floatingPointDecimalString ?? '0'}
                            priceCurrencyCode={product.currencyCode}
                            formatForm="short"
                            shouldCalculateCharmPrice={true}
                          />
                        </TextV2>
                      )}
                      <MsrpTooltip
                        msrpDescription={msrpDescription}
                        msrpTitle={msrpTitle}
                      />
                    </Flex>
                  </SpacingStack>
                ) : (
                  <>
                    <FormControl
                      label="Product URL"
                      isRequired
                      isInvalid={formik.touched.link && !!errors.link}
                      error={errorLink}
                    >
                      <InputV2
                        {...formik.getFieldProps('link')}
                        type="text"
                        endElement={isProductExtractionLoading ? <Spinner /> : undefined}
                        onPaste={e => {
                          extractDataFromUrl(e.clipboardData.getData('text'));
                        }}
                      />
                    </FormControl>
                    <FormControl
                      label={titleField}
                      isRequired
                      isInvalid={formik.touched.title && !!errors.title}
                      error={errorTitle}
                    >
                      {isProductExtractionLoading ? (
                        <Skeleton
                          height="3rem"
                          width={`100%`}
                        />
                      ) : (
                        <InputV2
                          {...formik.getFieldProps('title')}
                          onChange={e => {
                            handleInputChange(
                              e,
                              !!prefilledData,
                              formik.getFieldProps('title').onChange,
                              'ProductTitle',
                              RegistryItemSource.manualScanned,
                              prefilledData?.name || '',
                              prefilledData?.url,
                            );
                          }}
                        />
                      )}
                    </FormControl>
                  </>
                )}
                {!isCatalogItem ? (
                  <StyledPriceQtyContainer>
                    <FormControl
                      width="100%"
                      isRequired
                      isInvalid={!!errors.price}
                      error={errors.price}
                      label={
                        <StyledFlexPriceHeader height={18}>
                          {msrpLabel}
                          <Flex>
                            <TooltipV2
                              content={
                                <Box>
                                  <TextV2
                                    color="white"
                                    typographyVariant="label3"
                                  >
                                    {changeCurrencyTitle}
                                  </TextV2>
                                  <br />
                                  <TextV2
                                    color="white"
                                    typographyVariant="label2"
                                  >
                                    {currencyBodyText}
                                  </TextV2>
                                </Box>
                              }
                              placement="topEnd"
                            >
                              <TextV2>
                                <Help />
                              </TextV2>
                            </TooltipV2>
                          </Flex>
                        </StyledFlexPriceHeader>
                      }
                    >
                      {isProductExtractionLoading ? (
                        <Skeleton
                          height={`3rem`}
                          width={`100%`}
                        />
                      ) : prefilledData?.regularPrice ? (
                        <TextV2>
                          <FormattedCurrency
                            priceFloatingPointDecimalString={prefilledData.regularPrice}
                            priceCurrencyCode="usd"
                            formatForm="short"
                          />
                        </TextV2>
                      ) : (
                        <InputV2
                          {...formik.getFieldProps('price')}
                          onChange={e => {
                            handleInputChange(
                              e,
                              !!prefilledData,
                              formik.getFieldProps('price').onChange,
                              'ProductPrice',
                              RegistryItemSource.manualScanned,
                              prefilledData?.regularPrice || '',
                              prefilledData?.url,
                            );
                          }}
                          min="0"
                          onKeyDown={preventNonNumericalInput}
                          isFullWidth
                        />
                      )}
                    </FormControl>
                    <FormControl
                      width="100%"
                      label={qtyField}
                      isRequired
                      error={errors.qty}
                      isInvalid={!!errors.qty}
                    >
                      <InputV2
                        {...formik.getFieldProps('qty')}
                        type="number"
                        min="1"
                        onKeyDown={preventNonNumericalInput}
                        isFullWidth
                      />
                    </FormControl>
                  </StyledPriceQtyContainer>
                ) : (
                  <StyleQtyBox width="80px">
                    <FormControl
                      label={qtyField}
                      isRequired
                      error={errors.qty}
                      isInvalid={!!errors.qty}
                    >
                      <InputV2
                        {...formik.getFieldProps('qty')}
                        type="number"
                        min="1"
                        onKeyDown={preventNonNumericalInput}
                        // Don't allow to change quantity if group gifting was enabled
                        isDisabled={!!formik.getFieldProps('isGroupGiftingEnabled').value}
                      />
                    </FormControl>
                  </StyleQtyBox>
                )}
                {featureGetCashEquivalentEnabled && !isCatalogItem && (
                  <SpacingStack spacing={5}>
                    <SelectPurchaseOptions
                      isGroupGiftDisabled={!isSingleItem}
                      onValueChange={handleChangeFromPurchaseOption}
                      formik={formik}
                      isGroupGiftingFeatureEnabled={isGroupGiftingFeatureEnabled}
                      setPaymentMethod={setPaymentMethod}
                      setPaymentMethodUsername={setPaymentMethodUsername}
                      selectedPaymentMethod={selectedPaymentMethod}
                      product={product}
                      currency={currency}
                    />
                  </SpacingStack>
                )}
                <SpacingStack spacing={5}>
                  <TextV2
                    typographyVariant="label3"
                    color="black"
                  >
                    {markGiftAs}
                  </TextV2>
                  <Box>
                    <CheckboxV2
                      marginBottom={pxToRem(6)}
                      {...formik.getFieldProps('mustHave')}
                      isChecked={formik.getFieldProps('mustHave').value}
                      onChange={event => {
                        formik.getFieldProps('mustHave').onChange(event);
                        trackEnableMustHave(product, event.target.checked);
                      }}
                      typographyVariant="hed1"
                    >
                      {mostWanted}
                    </CheckboxV2>
                    <TextV2
                      typographyVariant="label1"
                      color="mono10"
                      marginLeft={pxToRem(28)}
                    >
                      {mostWantedDescription}
                    </TextV2>
                  </Box>
                  {!!isGroupGiftingFeatureEnabled && (!featureGetCashEquivalentEnabled || isCatalogItem) && (
                    <Box cursor={canRemoveProduct ? 'default' : 'not-allowed'}>
                      <CheckboxV2
                        marginBottom={pxToRem(6)}
                        {...formik.getFieldProps('isGroupGiftingEnabled')}
                        isChecked={isSingleItem ? formik.getFieldProps('isGroupGiftingEnabled').value : false}
                        isDisabled={!canRemoveProduct || !isSingleItem}
                        onChange={event => {
                          formik.getFieldProps('isGroupGiftingEnabled').onChange(event);
                          trackEnableGroupGifting(product, event.target.checked);
                        }}
                        typographyVariant="hed1"
                      >
                        <Flex alignItems="center">
                          {groupGift}
                          <TooltipV2
                            placement="topStart"
                            content={
                              <Box
                                width="480px"
                                typographyVariant="body2"
                              >
                                {groupGiftingTooltip}
                                <TextV2
                                  as="span"
                                  typographyVariant="body2"
                                  marginX="4px"
                                >
                                  {moreDetails}
                                </TextV2>
                                <LinkV2
                                  color="white"
                                  _visited={{ color: 'white' }}
                                  typographyVariant="body2"
                                  href={GROUP_GIFT_HELP_REDIRECTION_LINK}
                                  target="__blank"
                                >
                                  {GROUP_GIFT_HELP_REDIRECTION_LINK}
                                </LinkV2>
                              </Box>
                            }
                          >
                            <TooltipIcon>
                              <IconV2
                                size="sm"
                                color="mono8"
                                marginLeft={3}
                              >
                                <Help />
                              </IconV2>
                            </TooltipIcon>
                          </TooltipV2>
                        </Flex>
                      </CheckboxV2>
                      <TextV2
                        typographyVariant="label1"
                        color="mono10"
                        marginLeft={pxToRem(28)}
                      >
                        {groupGiftingDescription}
                      </TextV2>
                    </Box>
                  )}
                </SpacingStack>
                {!!isGroupGiftingFeatureEnabled && (!featureGetCashEquivalentEnabled || isCatalogItem) && (
                  <>
                    {!!formik.getFieldProps('isGroupGiftingEnabled').value && isSingleItem && (
                      <Box marginTop={6}>
                        <TextV2
                          typographyVariant="label3"
                          marginBottom={1}
                        >
                          {paymentSettings}
                        </TextV2>
                        <TextV2
                          typographyVariant="label1"
                          color="mono12"
                          marginBottom={4}
                        >
                          {selectPaymentMethod}
                        </TextV2>
                        <Flex
                          alignItems="flex-start"
                          justifyContent="center"
                          flexDirection={['column', 'column', 'row']}
                          rowGap={4}
                          columnGap={4}
                        >
                          <FormControl
                            error={formik.touched.paymentMethod && errors.paymentMethod ? errors.paymentMethod : undefined}
                            width={['100%', '100%', '50%']}
                          >
                            <>
                              <SelectV1
                                placeholder={paymentMethodLabel}
                                searchable={false}
                                options={localPaymentOptions}
                                value={selectedPaymentMethod}
                                onChange={e => {
                                  setPaymentMethod(e?.value as DonationFundPlatformTypeEnum);
                                  groupGiftingPaymentMethodSelect({ product, paymentMethod: e?.value });
                                }}
                              />
                              <LinkV1
                                href={getPaymentLink(formik.values.paymentMethod, formik.values.username)}
                                target="_blank"
                                underline="none"
                                style={{
                                  fontSize: '13px',
                                  lineHeight: '20px',
                                  opacity: formik.values.username ? 1 : 0,
                                  display: errors.username && (formik.touched.paymentMethod || formik.touched.username) ? 'none' : 'block',
                                }}
                                tabIndex={0}
                              >
                                {getPaymentLink(formik.values.paymentMethod, formik.values.username)}
                              </LinkV1>
                            </>
                          </FormControl>
                          <FormControl
                            error={formik.touched.username && errors.username ? errors.username : undefined}
                            isInvalid={!!formik.touched.username && !!errors.username}
                            width={['100%', '100%', '50%']}
                          >
                            <>
                              <Flex
                                alignItems="center"
                                overflow="hidden"
                                position="relative"
                                width={['100%', 'unset', 'unset']}
                              >
                                <InputV2
                                  {...formik.getFieldProps('username')}
                                  placeholder="username"
                                  tabIndex={0}
                                  style={{ padding: '0 16px 0 28px' }}
                                  onChange={e => setPaymentMethodUsername(e?.target.value)}
                                />
                                <StyledInputPrefix
                                  isFixedPrefix
                                  prefixOffset={15}
                                >
                                  {getPaymentUsernameFormat(formik.values.paymentMethod, formik.values.currency)}
                                </StyledInputPrefix>
                              </Flex>
                            </>
                          </FormControl>
                        </Flex>
                      </Box>
                    )}
                  </>
                )}
                {isPaymentMethodWarningShown && (
                  <TextV2
                    typographyVariant="label2"
                    color="negative6"
                    component="p"
                    marginTop={4}
                  >
                    {newPaymentMethodWarning}
                  </TextV2>
                )}
                <FormControl
                  marginTop={2}
                  label={noteField}
                  isRequired={false}
                  error={errors.note}
                >
                  <TextArea
                    {...formik.getFieldProps('note')}
                    placeholder={noteFieldPlaceholder}
                    resize={false}
                    name="note"
                  />
                </FormControl>
              </SpacingStack>
            </form>
          </ColumnsV2.Column>
        </ColumnsV2>
      </DialogV2.Body>
      <Divider margin="0px" />
      <DialogV2.Footer>
        <StyledButtonsContainer
          width={['100%', 'auto']}
          flexDirection={['column-reverse', 'row']}
        >
          {canRemoveProduct && (
            <ButtonV2
              intent="destructive"
              position={['relative', 'absolute', 'absolute']}
              marginLeft={[0, 24, 24]}
              left={0}
              variant="ghost"
              onClick={handleDeleteRegistryItem}
              width={['100%', pxToRem(120)]}
            >
              {deleteButton}
            </ButtonV2>
          )}
          <CancelButton
            disabled={registryItemLoading}
            onClick={handleCancel}
            width={['100%', pxToRem(120)]}
          >
            {cancelButton}
          </CancelButton>
          <ButtonV2
            intent="neutral"
            onClick={handleConfirm}
            disabled={registryItemLoading || isProductExtractionLoading}
            type="submit"
            width={['100%', pxToRem(120)]}
          >
            {registryItemLoading || isProductExtractionLoading ? <Spinner /> : confirmButton}
          </ButtonV2>
        </StyledButtonsContainer>
      </DialogV2.Footer>
      <AppPaymentDisclaimerDialog
        isOpen={isOpenDisclaimerDialog}
        paymentLink={getPaymentLink(formik.values.paymentMethod, formik.values.username)}
        paymentUsername={formik.values.username || ''}
        paymentMethod={formik.values.paymentMethod}
        onClose={onCloseDisclaimerDialog}
        formik={disclaimerFormik}
        currencyCode={currency}
      />
    </>
  );
};

export const EditGiftDialog: React.FC<EditGiftDialogProps> = props => {
  const { isOpen, product } = props;
  const isNewGift = !product?.id;

  return (
    <DialogV2
      overrides={{
        CloseButton: {
          props: { top: 3 },
        },
      }}
      id={isNewGift ? 'AddGiftDialog' : 'EditGiftDialog'}
      isOpen={isOpen}
      size="3xl"
      disableFocusLock={true}
    >
      <DialogInternal {...props} />
    </DialogV2>
  );
};
